/**
 * Change-only playback sampling: poll the playback position on a schedule,
 * derive a value from it, and notify/re-render only when that derived value
 * actually changes. Replaces the per-word 20fps `setState` loops and the
 * fixed-100ms `setInterval` roots that re-rendered whole screens every tick.
 *
 * The controller (`createChangeOnlySampler`) is pure and drives both the React
 * hook and the web highlight manager (a non-render consumer), so its behavior
 * is tested in the node vitest environment.
 */

import { useAtomValue } from '@effect/atom-react';
import {
  createContext,
  use,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from 'react';
import { Atom } from 'effect/reactivity';
import { clamp } from '@listening/utils/ts-utils';
import { docPackAnchorsEqual } from '../doc-pack/text-layout';
import { useAudioPrefsStore } from '../stores/audio-preferences-store';
import type { DocPackPlaybackAnchor } from './core';
import { highlightLagRecorder } from './highlight-lag';
import { useAudioChunkSelector } from './hooks';
import { getChunkTimingIndex, type ChunkTimingSource } from './timing-index';
import { Predicate } from 'effect';

/** Whether the app's UI can be seen. Sampling only feeds what is drawn, so it
 * stops while this is false and resamples when it turns true. Mobile sets it
 * false while the app is backgrounded, which includes a locked screen with
 * audio playing; nothing else writes it. */
export const playbackSurfaceVisibleAtom = Atom.make(true).pipe(Atom.keepAlive);

/** Whether the screen this subtree renders in can be seen. A navigator
 * provides `false` under a screen another screen covers, which stops
 * sampling there the way `playbackSurfaceVisibleAtom` does for the app. */
export const PlaybackScreenVisibleContext = createContext(true);

function usePlaybackSurfaceVisible() {
  const appVisible = useAtomValue(playbackSurfaceVisibleAtom);
  return use(PlaybackScreenVisibleContext) && appVisible;
}

/** STRUCTURAL position source — never the web `AudioPoolPlayerHandle` type, so
 * this stays shareable across platforms. */
export type PlaybackPositionSource = {
  readonly isPlaying: boolean;
  readonly getPositionMs: () => number;
  /** Monotonic seek counter; a bump forces an immediate resample even while
   * paused (a within-chunk seek moves the position without any other
   * observable change). Optional: sources that don't track it never resample
   * on seek, which is the prior behavior. */
  readonly seekCount?: number;
};

/** A sampling loop primitive: `start(cb)` begins ticking `cb`, returns a stop. */
export type PlaybackSampleScheduler = {
  start: (cb: () => void) => () => void;
};

/** rAF-driven scheduler throttled to `fps` (web). SSR-safe: no-op when
 * `requestAnimationFrame` is absent. */
export function createRafScheduler(fps = 20): PlaybackSampleScheduler {
  return {
    start: (cb) => {
      if (!Predicate.isFunction(globalThis.requestAnimationFrame))
        return () => undefined;
      const minIntervalMs = 1000 / fps;
      let handle = 0;
      let last = Number.NEGATIVE_INFINITY;
      const loop = (now: number) => {
        handle = requestAnimationFrame(loop);
        if (now - last >= minIntervalMs) {
          last = now;
          cb();
        }
      };
      handle = requestAnimationFrame(loop);
      return () => cancelAnimationFrame(handle);
    },
  };
}

/** Fixed-interval scheduler (RN / default). */
export function createIntervalScheduler(ms: number): PlaybackSampleScheduler {
  return {
    start: (cb) => {
      const id = setInterval(cb, ms);
      return () => clearInterval(id);
    },
  };
}

export type ChangeOnlySampler = {
  /** Begin the sampling loop (idempotent). */
  start: () => void;
  /** Stop the loop. */
  stop: () => void;
  /** Sample once immediately, off the loop (mount / seek-while-paused). */
  resample: () => void;
  resync: () => void;
};

export function createChangeOnlySampler<T>(opts: {
  getPositionMs: () => number;
  derive: (positionMs: number) => T;
  equals: (a: T, b: T) => boolean;
  onChange: (value: T) => void;
  scheduler: PlaybackSampleScheduler;
}): ChangeOnlySampler {
  let stopLoop: (() => void) | null = null;
  let hasValue = false;
  let current: T;

  const sampleOnce = () => {
    const next = opts.derive(opts.getPositionMs());
    if (hasValue && opts.equals(current, next)) return;
    hasValue = true;
    current = next;
    opts.onChange(next);
  };

  return {
    start: () => {
      if (stopLoop) return;
      stopLoop = opts.scheduler.start(sampleOnce);
    },
    stop: () => {
      stopLoop?.();
      stopLoop = null;
    },
    resample: sampleOnce,
    resync: () => {
      hasValue = false;
      sampleOnce();
    },
  };
}

/** Poll cadence in ms: faster at higher playback rates so a word never lasts
 * less than one sample (kills the high-speed skip ceiling). 33ms at 3x. */
export function playbackSampleCadenceMs(
  playbackRate: number | undefined,
): number {
  if (!playbackRate || playbackRate <= 0) return 100;
  return clamp(100 / playbackRate, 33, 100);
}

export function usePlaybackDerived<T>(
  player: PlaybackPositionSource,
  derive: (positionMs: number) => T,
  options?: {
    /** Re-render ONLY when this returns false. Default `Object.is`. */
    equals?: (a: T, b: T) => boolean;
    /** Cadence = clamp(100/rate, 33, 100)ms. */
    playbackRate?: number;
    /** `false` = no polling (e.g. a closed sheet). */
    enabled?: boolean;
    /** Change → immediate resample even while paused (mount / seek / chunk change). */
    resampleKey?: unknown;
    /** `true` keeps the last value without reading the position; release
     * resamples immediately. */
    hold?: boolean;
    /** Scheduler override (web passes rAF); default is interval at cadence. */
    scheduler?: PlaybackSampleScheduler;
  },
): T {
  const {
    equals,
    playbackRate,
    enabled = true,
    resampleKey,
    hold = false,
    scheduler,
  } = options ?? {};
  const cadence = playbackSampleCadenceMs(playbackRate);
  const { isPlaying, getPositionMs, seekCount } = player;
  const surfaceVisible = usePlaybackSurfaceVisible();

  const [value, setValue] = useState<T>(() => derive(getPositionMs()));

  // Resample DURING render when the chunk (resampleKey) or a seek changes —
  // the effect below also resamples, but effects run after commit, which
  // would let one frame reach the screen pairing the new chunk with a value
  // derived from the old one (the read-along boundary scroll-jump).
  // The key is state, not a ref: React can discard a render (a suspended or
  // interrupted attempt), which throws away the queued setValue. A ref
  // mutation would survive that, so the retry render would skip the resample
  // and commit exactly the stale pairing this guard exists to prevent.
  const [syncKey, setSyncKey] = useState<
    readonly [unknown, number | undefined, boolean]
  >([resampleKey, seekCount, hold]);
  if (
    !Object.is(syncKey[0], resampleKey) ||
    syncKey[1] !== seekCount ||
    syncKey[2] !== hold
  ) {
    setSyncKey([resampleKey, seekCount, hold]);
    if (!hold) {
      const next = derive(getPositionMs());
      if (!(equals ? equals(value, next) : Object.is(value, next)))
        setValue(next);
    }
  }

  // The sampler sees the latest derive and position source through effect
  // events, so changing them doesn't restart the sampling effect. A chunk
  // change or seek resyncs it after commit: a sampler that kept its previous
  // value would skip a correction equal to it. `equals` is read in the effect
  // because the setValue updater can run during render.
  const readPositionMs = useEffectEvent(() => getPositionMs());
  const deriveLatest = useEffectEvent((positionMs: number) =>
    derive(positionMs),
  );
  const makeScheduler = useEffectEvent(
    (cadenceMs: number) => scheduler ?? createIntervalScheduler(cadenceMs),
  );

  const samplerRef = useRef<ChangeOnlySampler | null>(null);

  useEffect(() => {
    if (hold) return;
    const eq = (a: T, b: T) => (equals ? equals(a, b) : Object.is(a, b));
    const sampler = createChangeOnlySampler<T>({
      getPositionMs: readPositionMs,
      derive: deriveLatest,
      equals: eq,
      onChange: (next) => setValue((prev) => (eq(prev, next) ? prev : next)),
      scheduler: makeScheduler(cadence),
    });
    samplerRef.current = sampler;
    // Sample once immediately on every (re)run — covers mount, a change of
    // play state and the UI becoming visible — then loop only while actively
    // playing on a visible UI.
    sampler.resample();
    if (isPlaying && enabled && surfaceVisible) sampler.start();
    return () => {
      sampler.stop();
      samplerRef.current = null;
    };
  }, [isPlaying, enabled, surfaceVisible, cadence, equals, hold]);

  const resyncAfterCommit = useEffectEvent(
    (_key: unknown, _seekCount: number | undefined) => {
      samplerRef.current?.resync();
    },
  );

  useEffect(() => {
    resyncAfterCommit(resampleKey, seekCount);
  }, [resampleKey, seekCount]);

  return value;
}

/**
 * Highlight-lag sample on every committed active-word change:
 * `position at commit − word start`. Runs as an effect so the position read
 * happens AFTER React committed the highlight — the render+commit latency is
 * exactly what the metric must include. Seeks (seekCount bump) and paused
 * resamples are excluded; a forward jump of more than one word while playing
 * counts the words the highlight never showed. Several read-along hooks are
 * mounted over one chunk (player screen, mini player, pdf reader), so each
 * reports the same advance; the recorder keeps the first per identity.
 */
function useHighlightLagSample(
  player: PlaybackPositionSource,
  chunk: ChunkTimingSource | null | undefined,
  wordStartMs: number | undefined,
  wordIndex: number | null,
) {
  const readPlayer = useEffectEvent(() => player);
  // Sampling stops while the UI is hidden, so the first word after it shows
  // again can jump ahead; that jump is not highlight lag or skipped words.
  const surfaceVisible = usePlaybackSurfaceVisible();
  const readSurfaceVisible = useEffectEvent(() => surfaceVisible);
  const hiddenSinceLastSampleRef = useRef(false);
  useEffect(() => {
    if (!surfaceVisible) hiddenSinceLastSampleRef.current = true;
  }, [surfaceVisible]);
  const previousRef = useRef<{
    chunk: ChunkTimingSource | null | undefined;
    startMs: number | undefined;
    index: number | null;
    seekCount: number | undefined;
  } | null>(null);

  useEffect(() => {
    const previous = previousRef.current;
    const committedPlayer = readPlayer();
    const seekCount = committedPlayer.seekCount;
    previousRef.current = {
      chunk,
      startMs: wordStartMs,
      index: wordIndex,
      seekCount,
    };
    if (!readSurfaceVisible()) return;
    if (hiddenSinceLastSampleRef.current) {
      hiddenSinceLastSampleRef.current = false;
      return;
    }
    if (wordStartMs === undefined) return;
    if (!committedPlayer.isPlaying) return;
    const sameStream =
      previous !== null &&
      previous.chunk === chunk &&
      previous.seekCount === seekCount;
    if (sameStream && previous.startMs === wordStartMs) return;
    const skipped =
      sameStream &&
      previous.index !== null &&
      wordIndex !== null &&
      wordIndex > previous.index
        ? wordIndex - previous.index - 1
        : 0;
    highlightLagRecorder.record({
      chunkKey: chunk,
      startMs: wordStartMs,
      seekCount,
      lagMs: committedPlayer.getPositionMs() - wordStartMs,
      skipped,
    });
  }, [chunk, wordStartMs, wordIndex]);
}

/** Change-only doc-pack anchor for the current chunk.
 *
 * Held while a chunk loads: until the engine has placed the playhead, the
 * transport position belongs to the previous chunk or to the new chunk's top,
 * and an anchor derived from it sends read-along follow on a detour before
 * the real target. The anchor is document-absolute, so the held one stays
 * correct across the chunk change, but not across a load of another document,
 * and a reader that mounts mid-load has no settled anchor to hold. */
export function useDocPackPlaybackAnchor(
  player: PlaybackPositionSource,
  chunk: ChunkTimingSource | null | undefined,
): DocPackPlaybackAnchor | undefined {
  const playbackRate = useAudioPrefsStore((s) => s.playbackRate);
  const isLoadingAudio = useAudioChunkSelector((s) => s.isLoadingAudio);
  const audioId = useAudioChunkSelector((s) => s.audioId);
  const [settledAudioId, setSettledAudioId] = useState(
    isLoadingAudio ? undefined : audioId,
  );
  if (!isLoadingAudio && settledAudioId !== audioId) setSettledAudioId(audioId);
  const anchor = usePlaybackDerived(
    player,
    (positionMs) => getChunkTimingIndex(chunk).docPackAnchorAt(positionMs),
    {
      equals: docPackAnchorsEqual,
      playbackRate,
      resampleKey: chunk,
      hold: isLoadingAudio,
    },
  );
  const anchorStartMs =
    anchor === undefined
      ? undefined
      : getChunkTimingIndex(chunk).docPackAnchorStartMsAt(anchor);
  useHighlightLagSample(player, chunk, anchorStartMs, null);
  return isLoadingAudio && settledAudioId !== audioId ? undefined : anchor;
}

/** Change-only legacy active word, with the chunk it indexes into.
 *
 * The index is chunk-relative, so a load holds it together with its chunk and
 * chunk ids: the read-along keeps the old chunk's word until the engine has
 * placed the playhead in the new one (see `useDocPackPlaybackAnchor`). */
export function useLegacyActiveWord<Chunk extends ChunkTimingSource>(
  player: PlaybackPositionSource,
  chunk: Chunk | null | undefined,
) {
  const playbackRate = useAudioPrefsStore((s) => s.playbackRate);
  const isLoadingAudio = useAudioChunkSelector((s) => s.isLoadingAudio);
  const chapterId = useAudioChunkSelector((s) => s.chapterId);
  const ttsId = useAudioChunkSelector((s) => s.ttsId);
  const [held, setHeld] = useState(() =>
    isLoadingAudio
      ? { chunk: undefined, chapterId: undefined, ttsId: undefined }
      : { chunk, chapterId, ttsId },
  );
  if (
    !isLoadingAudio &&
    (held.chunk !== chunk ||
      held.chapterId !== chapterId ||
      held.ttsId !== ttsId)
  )
    setHeld({ chunk, chapterId, ttsId });
  const source = isLoadingAudio ? held : { chunk, chapterId, ttsId };
  const activeWordIdx = usePlaybackDerived(
    player,
    (positionMs) =>
      getChunkTimingIndex(source.chunk).activeWordIdxAt(positionMs),
    { playbackRate, resampleKey: source.chunk, hold: isLoadingAudio },
  );
  useHighlightLagSample(
    player,
    source.chunk,
    activeWordIdx < 0
      ? undefined
      : getChunkTimingIndex(source.chunk).legacyWordStartMsAt(activeWordIdx),
    activeWordIdx < 0 ? null : activeWordIdx,
  );
  return { activeWordIdx, ...source };
}
